import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  api,
  approveReviewItemL1,
  approveReviewItemL2,
  rejectReviewItem,
} from "../../services/api";
import EntityReadOnlyView from "../../components/EntityReadOnlyView";

const PENDING_STATES = new Set(["pending_l1", "pending_l2", "changes_requested"]);

function ReviewItemActionsPanel({ item, tokens, isInlineLight, onActionDone, readOnly }) {
  const [note, setNote] = useState("");
  const [reasonCode, setReasonCode] = useState("not_suitable");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const pending = PENDING_STATES.has(item?.state);
  const actionable = pending && item?.can_act !== false;

  const runApprove = async () => {
    setBusy("approve");
    setError("");
    try {
      if (item.state === "pending_l2") await approveReviewItemL2(item.id, note);
      else await approveReviewItemL1(item.id, note);
      onActionDone?.();
    } catch (e) {
      setError(e?.message || "Approve failed.");
    } finally {
      setBusy("");
    }
  };

  const runReject = async () => {
    setBusy("reject");
    setError("");
    try {
      await rejectReviewItem(item.id, reasonCode, note);
      onActionDone?.();
    } catch (e) {
      setError(e?.message || "Reject failed.");
    } finally {
      setBusy("");
    }
  };

  if (!actionable || readOnly) {
    if (!actionable && pending && item?.can_act === false) {
      return (
        <p className="text-xs text-slate-600">
          You cannot approve this item - it is assigned to another approver group, or you
          submitted it and your policy blocks self-approval.
        </p>
      );
    }
    return null;
  }

  return (
    <div
      className={
        "rounded-xl border border-slate-200 bg-white p-4 space-y-3" +
        (isInlineLight ? " shadow-sm" : "")
      }
    >
      <p className="text-sm font-medium text-slate-900">Decision</p>
      <textarea
        value={note}
        disabled={Boolean(busy)}
        onChange={(e) => setNote(e.target.value)}
        rows={2}
        placeholder="Comment (optional for approve, shown in audit trail)"
        className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
      />
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-xs text-slate-600">Reject reason:</label>
        <select
          value={reasonCode}
          disabled={Boolean(busy)}
          onChange={(e) => setReasonCode(e.target.value)}
          className="text-xs px-2 py-1 rounded border border-slate-300"
        >
          <option value="not_suitable">Not right for the map</option>
          <option value="duplicate">Already on the map</option>
          <option value="incomplete">Incomplete information</option>
          <option value="other">Other</option>
        </select>
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={Boolean(busy)}
          onClick={() => void runApprove()}
          className={tokens.button.success}
        >
          {busy === "approve"
            ? "Approving..."
            : item.state === "pending_l2"
              ? "Approve (second review)"
              : "Add to map"}
        </button>
        <button
          type="button"
          disabled={Boolean(busy)}
          onClick={() => void runReject()}
          className={tokens.button.secondary}
        >
          {busy === "reject" ? "Rejecting..." : "Reject"}
        </button>
      </div>
      {error ? <p className="text-xs text-red-600">{error}</p> : null}
    </div>
  );
}

export default function ApprovalItemDetailView({
  item,
  tokens,
  isInlineLight = true,
  onBack,
  onActionDone,
  readOnly = false,
  hideInlineBack = false,
}) {
  const [entityFull, setEntityFull] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const entityId = useMemo(() => {
    if (String(item?.item_kind || "") !== "entity") return null;
    return (
      String(item?.source_record_id || item?.proposed_payload?.id || "").trim() || null
    );
  }, [item]);

  const isEntity = Boolean(entityId);
  const summary = item?.summary || {};
  const title = summary.title || item?.proposed_payload?.name || "Submission";
  const submissionComment = String(item?.evidence_payload?.submission_comment || "").trim();

  const loadEntity = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    setLoadError("");
    try {
      const full = await api.getEntity(entityId, {
        includeRelationships: true,
        includeRelatedEntities: true,
        includeUnapproved: true,
        architectureExplorer: true,
        reviewItemId: item?.id || null,
      });
      setEntityFull(full);
    } catch (err) {
      setEntityFull(null);
      setLoadError(err?.message || "Could not load entity details.");
    } finally {
      setLoading(false);
    }
  }, [entityId, item?.id]);

  useEffect(() => {
    if (isEntity) void loadEntity();
    else {
      setEntityFull(null);
      setLoadError("");
      setLoading(false);
    }
  }, [isEntity, loadEntity]);

  const allEntitiesMap = useMemo(() => {
    const map = {};
    if (entityFull?.id) map[entityFull.id] = entityFull;
    const related = entityFull?.related_entities || {};
    Object.entries(related).forEach(([id, ent]) => {
      if (id && ent) map[id] = ent;
    });
    return map;
  }, [entityFull]);

  const submitterNoteClass = tokens.surface.mutedText + " mt-1 text-sm";

  return (
    <div className="space-y-4">
      <div>
        {!hideInlineBack ? (
          <button
            type="button"
            onClick={onBack}
            className="mb-2 text-xs text-slate-600 hover:text-slate-900"
          >
            &larr; Back to approvals
          </button>
        ) : null}
        <h2 className={`${hideInlineBack ? "text-lg" : "text-2xl"} font-semibold`}>
          {title}
        </h2>
        {submissionComment ? (
          <p className={submitterNoteClass}>Submitter note: {submissionComment}</p>
        ) : null}
      </div>

      {isEntity ? (
        loading ? (
          <p className={tokens.surface.mutedText}>Loading entity details...</p>
        ) : loadError ? (
          <p className="text-sm text-red-600">{loadError}</p>
        ) : entityFull ? (
          <div className="v2-explorer-light rounded-xl border border-slate-200 bg-white overflow-hidden">
            <EntityReadOnlyView
              embedded
              architectureExplorerView
              disableLiveRefresh
              canEdit={false}
              entity={entityFull}
              relationships={
                Array.isArray(entityFull?.relationships) ? entityFull.relationships : []
              }
              relationshipsLoading={false}
              allEntities={allEntitiesMap}
              onClose={null}
            />
          </div>
        ) : null
      ) : (
        <div className={tokens.surface.card + " p-4"}>
          <p className={tokens.surface.mutedText}>
            Detail view is not available for this submission type.
          </p>
        </div>
      )}

      <ReviewItemActionsPanel
        item={item}
        tokens={tokens}
        isInlineLight={isInlineLight}
        onActionDone={onActionDone}
        readOnly={readOnly}
      />
    </div>
  );
}
